import * as React from 'react'
import { connect } from 'react-redux'
import withQueryParams, { InjectedQueryParams } from 'react-router-query-params'

import Moment from 'react-moment'
import Pagination from 'react-js-pagination'
import { Table, Collapse } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { RootState } from '../../store/rootReducer'
import { Scan } from '../../lib/interfaces/Scan.interface'
import { Employee } from '../../lib/interfaces/Employee.interface'
import { Location } from '../../lib/interfaces/Location.interface'
import { ScansByEmployee } from '../../lib/interfaces/ScansByEmployee.interface'
import { BaseReportFilterParameters } from '../../lib/interfaces/BaseReportFilterParameters.interface'

import * as scansActions from '../../store/scans/actions'
import * as employeesActions from '../../store/employees/actions'

import Breadcrumbs from '../Breadcrumbs.component'
import ScanFilter from './components/ScanFilter.component'

import * as authSelectors from 'src/store/auth/selectors'

import { numberArrayDefaultValue } from '../../utils/query-params'

const get = require('lodash/get')
const map = require('lodash/map')
const size = require('lodash/size')
const first = require('lodash/first')
const slice = require('lodash/slice')
const isEmpty = require('lodash/isEmpty')

const moment = require('moment')

const PAGE_SIZE = 5000

interface ScanListProps extends InjectedQueryParams<BaseReportFilterParameters> {
  scans?: Scan[]
  isFetchingScans: boolean
  principalLocation?: Location
  scansByEmployee?: ScansByEmployee[]
  filterParams: BaseReportFilterParameters
  setCurrentEmployee: (employee: Employee) => void
  setFilterParams: (filterParams: BaseReportFilterParameters) => void
  fetchScansList: (next?: (err?: any, scans?: Scan[]) => void) => void
}

interface ScanListState {
  currentPage: number
}

class ScanListPage extends React.PureComponent<ScanListProps, ScanListState> {
  state: ScanListState = {
    currentPage: 0
  }

  componentDidMount() {
    const { queryParams, setFilterParams, fetchScansList } = this.props
    
    setFilterParams(queryParams)

    if (queryParams.location !== undefined) {
      fetchScansList()
    }
  }

  onFiltersChange = (filters: BaseReportFilterParameters) => {
    this.props.setFilterParams(filters)
    this.props.setQueryParams(filters)
    this.props.fetchScansList()

    this.setState({ currentPage: 0 })
  }

  get scansToShow() {
    const { scans } = this.props
    const { currentPage } = this.state

    const initialIndex = currentPage * PAGE_SIZE
    return slice(scans, initialIndex, initialIndex + PAGE_SIZE)
  }

  renderPagination() {
    const { scans } = this.props
    const { currentPage } = this.state

    const arraySize = size(scans)
    if (arraySize <= PAGE_SIZE) {
      return null
    }

    return (
      <div className="d-flex justify-content-end">
        <Pagination
          itemClass="page-item"
          linkClass="page-link"
          totalItemsCount={arraySize}
          activePage={currentPage + 1}
          itemsCountPerPage={PAGE_SIZE}
          onChange={page => this.setState({ currentPage: page - 1 })}
        />
      </div>
    )
  }

  render() {
    const { queryParams, isFetchingScans } = this.props

    return (
      <div className="d-flex flex-column min-height-100">
        <Breadcrumbs breadcrumbs={[
          { name: 'Dashboard', link: '/dashboard' },
          { name: 'Scan Reports', active: true },
        ]} />

        <div className="page applications-page flex-grow-1 min-height-0">
          <section>
            <div className="d-flex align-items-center mb-3">
              <h5 className="title mb-0">Scan Reports</h5>
              {isFetchingScans && <FontAwesomeIcon spin size="lg" className="ml-3" icon={['fas', 'spinner']} />}
            </div>

            <ScanFilter
              filterParams={queryParams}
              onChange={this.onFiltersChange}
            />

            <Table hover className="ggtable">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>ID</th>
                  <th style={{ width: '20%' }}>Time</th>
                  <th style={{ width: '25%' }}>Position</th>
                  <th style={{ width: '35%' }}>VIN</th>
                </tr>
              </thead>
              
              <tbody>
                {map(this.scansToShow, scan => (
                  <tr key={scan.id}>
                    <td>{scan.driverId}</td>
                    <td><Moment date={scan.timeScanned} format="hh:mm:ss A" /></td>
                    <td>{get(scan, 'position.name', '-')}</td>
                    <td>{scan.vin || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {this.renderPagination()}
          </section>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  scans: state.scans.scans,
  filterParams: state.scans.filterParams,
  isFetchingScans: state.scans.isFetchingScans,
  scansByEmployee: state.scans.scansByEmployee,
  principalLocation: authSelectors.getPrincipalLocation(state)
})

const mapDispatchToProps = (dispatch) => ({
  setCurrentEmployee: (employee: Employee) => dispatch(employeesActions.setCurrentEmployee(employee)),
  setFilterParams: (filterParams: BaseReportFilterParameters) =>
    dispatch(scansActions.setScansFilterParams(filterParams)),
  fetchScansList: (next?: (err?: any, scans?: Scan[]) => void) =>
    dispatch(scansActions.fetchScansList(next))
})

export default connect(mapStateToProps, mapDispatchToProps)(
  withQueryParams<BaseReportFilterParameters, ScanListProps>({
    stripUnknownKeys: true,
    keys: {
      location: {
        validate: () => false,
        default: (value, props) => {
          if (value) { return first(numberArrayDefaultValue(value)) }

          if (props.filterParams.location !== undefined) {
            return props.filterParams.location
          }
          
          if (props.principalLocation !== undefined && props.principalLocation.code !== 'MCO') {
            return props.principalLocation.id
          }

          return undefined
        }
      },
      position: {
        validate: () => false,
        default: (value, props) => {
          if (value) { return numberArrayDefaultValue(value) }
          return props.filterParams.position
        }
      },
      fromDate: {
        default: (value, props) => props.filterParams.fromDate,
        validate: value => {
          if (!value) { return false }
          return moment(value).isValid()
        }
      }
    }
  }
)(ScanListPage))


// WEBPACK FOOTER //
// ./src/routes/scans/ScanList.page.tsx